<script setup>
import { useRoute } from 'vue-router'
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue'
import { onValue, push, ref as fref, remove, serverTimestamp, set, update } from 'firebase/database'
import { database } from '@/firebase.js'
import { useAuth } from '@/stores/auth.js'
import { notNull } from '@/rules.js'
import LoginComp from '@/components/LoginComp.vue'
import CreateRoom from '@/components/CreateRoom.vue'
import Crown from '@/components/icons/Crown.vue'
import Question from '@/components/icons/Question.vue'
import Coffee from '@/components/icons/Coffee.vue'
import ConfettiExplosion from 'vue-confetti-explosion'
import ThreeDot from '@/components/icons/ThreeDot.vue'

const route = useRoute()
const auth = useAuth()
const points = ref([1, 2, 5, 8, 13, -1, -2])
const isAddStoryValid = ref(false)
const storyTitle = ref()
const showLoginDialog = ref(false)
const showCreateRoom = computed(() => !showLoginDialog.value && !route.params.id)
const isUserAdmin = computed(() => room.value.owner === auth.id)

const showConfetti = ref(false)

const room = ref()
const voteAverage = computed(() => {
  if (!room.value || !room.value.activeStory?.votes) {
    return 0
  }
  const total = Object.values(room.value.activeStory.votes)
    .filter((item) => item > 0)
    .reduce((sum, number) => sum + number, 0)
  return total / Object.values(room.value.activeStory.votes).length
})

const currentVote = computed(() => {
  return room.value.activeStory?.votes ? room.value.activeStory.votes[auth.id] : -999
})
const allUserVoted = computed(() => {
  if (!room.value || !room.value.activeStory || !room.value.activeStory.votes) {
    return false
  }

  return Object.values(room.value.users)
    .filter((user) => !user.observer)
    .every((user) => Object.keys(room.value.activeStory.votes).includes(user.id))
})

watch(
  () => allUserVoted.value,
  async (newVal) => {
    if (newVal === true) {
      await nextTick()
      showConfetti.value = newVal
    }
  }
)

watch(
  () => route.params.id,
  async (newVal) => {
    if (!newVal) {
      return
    }
    const ref = fref(database, 'rooms/' + newVal)
    await onValue(ref, (snapshot) => {
      room.value = snapshot.val()
      if (
        auth.id &&
        room.value &&
        room.value.users &&
        !Object.values(room.value.users).some((item) => item.id === auth.id)
      ) {
        addUserToRoom()
      }
      auth.observer = room.value.users[auth.id].observer
    })
  },
  { immediate: true }
)

onBeforeMount(() => {
  if (!auth.isLoggedIn) {
    showLoginDialog.value = true
  }
})

const vote = async (point) => {
  const votesRef = fref(database, `rooms/${route.params.id}/activeStory/votes`)
  await update(votesRef, {
    [auth.id]: point
  })
}

const isVoted = (userId) => {
  return (
    room.value.activeStory &&
    room.value.activeStory.votes &&
    Object.keys(room.value.activeStory.votes).includes(userId)
  )
}

const start = () => {
  if (!room.value.stories || room.value.stories.length < 1) {
    alert('Add story')
    return
  }
  const roomRef = fref(database, `rooms/${route.params.id}`)
  update(roomRef, {
    started: true,
    activeStory: Object.values(room.value.stories)[0]
  })
}

const restartVote = async () => {
  const votesRef = fref(database, `rooms/${route.params.id}/activeStory/votes`)
  await set(votesRef, {})

  const storyRef = fref(database, `rooms/${route.params.id}/stories/${room.value.activeStory.id}`)
  await update(storyRef, { estimated: false, estimation: 0 })
}

const next = async () => {
  let currentStoryRef = fref(
    database,
    `rooms/${route.params.id}/stories/${room.value.activeStory.id}`
  )

  await update(currentStoryRef, { estimated: true, estimation: voteAverage.value }).then(() =>
    moveNext()
  )
}

const skip = async () => {
  let currentStoryRef = fref(
    database,
    `rooms/${route.params.id}/stories/${room.value.activeStory.id}`
  )
  await update(currentStoryRef, { estimated: true, estimation: 0 }).then(() => moveNext())
}

const moveNext = async () => {
  let storyKeys = Object.keys(room.value.stories)
  const nextStoryIndex = storyKeys.indexOf(room.value.activeStory.id) + 1
  if (nextStoryIndex > storyKeys.length - 1) {
    const activeStoryRef = fref(database, `rooms/${route.params.id}/activeStory`)
    await remove(activeStoryRef)

    const roomRef = fref(database, `rooms/${route.params.id}`)
    await update(roomRef, { finished: true })

    return
  }

  const newActiveStory = room.value.stories[storyKeys[nextStoryIndex]]
  const activeStoryRef = fref(database, `rooms/${route.params.id}/activeStory`)
  await set(activeStoryRef, newActiveStory)
  showConfetti.value = false
}

const addStory = async () => {
  if (!isAddStoryValid.value) {
    return
  }

  const storyRef = fref(database, `rooms/${route.params.id}/stories`)
  const newStoryRef = push(storyRef)

  await set(newStoryRef, {
    id: newStoryRef.key,
    created_at: serverTimestamp(),
    title: storyTitle.value,
    estimated: false
  }).then(() => {
    storyTitle.value = ''
  })
}

const removeStory = async (id) => {
  const storyRef = fref(database, `rooms/${route.params.id}/stories/${id}`)
  await remove(storyRef)
}

const observe = async () => {
  const usersRef = fref(database, `rooms/${route.params.id}/users/${auth.id}`)
  await update(usersRef, {
    observer: !room.value.users[auth.id].observer
  })
}

const onUsernameSave = async () => {
  showLoginDialog.value = false
  if (room.value) {
    await addUserToRoom()
  }
}

const addUserToRoom = async () => {
  const usersRef = fref(database, `rooms/${route.params.id}/users/${auth.id}`)
  await set(usersRef, {
    id: auth.id,
    username: auth.username,
    observer: false
  })
}

const makeObserver = async (userId) => {
  const usersRef = fref(database, `rooms/${route.params.id}/users/${userId}`)
  await update(usersRef, {
    observer: !room.value.users[userId].observer
  })
}
</script>

<template>
  <v-container>
    <v-card v-if="room" :loading="!room">
      <v-toolbar>
        <v-toolbar-title>
          {{ room?.activeStory?.title }}
        </v-toolbar-title>
      </v-toolbar>
      <v-container>
        <v-alert v-if="room.finished" color="success" class="mb-4"> Estimation is done...</v-alert>
        <v-alert v-if="!room.activeStory && !room.finished" color="warning" class="mb-4">
          The voting has not started yet...
        </v-alert>

        <!-- ESTIMATION CARDS -->
        <v-row v-if="!allUserVoted && room.activeStory && !auth.observer" class="position-relative">
          <v-col v-for="point in points" :key="point">
            <v-card
              min-width="100px"
              min-height="150px"
              class="d-flex justify-center align-center text-primary"
              :color="point === currentVote ? 'secondary' : null"
              :disabled="!room.activeStory"
              elevation="5"
              hover
              @click="() => vote(point)">
              <h1 v-if="point === -1">
                <question />
              </h1>
              <h1 v-else-if="point === -2">
                <coffee />
              </h1>
              <h1 v-else>{{ point }}</h1>
            </v-card>
          </v-col>
        </v-row>

        <!-- RESULT -->
        <v-row
          v-if="room.activeStory && (allUserVoted || (auth.observer && isUserAdmin))"
          class="d-flex justify-center flex-column align-center">
          <confetti-explosion v-if="showConfetti" :duration="2500" />
          <h1 class="text-primary">AVERAGE : {{ voteAverage }}</h1>
          <v-list color="primary" class="w-66">
            <v-list-item
              v-for="user in room.users"
              :key="user.id"
              variant="outlined"
              rounded
              active
              class="mt-2">
              <template #append>
                <div v-if="room.activeStory?.votes" class="text-h5">
                  <span v-if="room?.activeStory?.votes[user.id] === -1"> <question /></span>
                  <span v-else-if="room?.activeStory?.votes[user.id] === -2"><coffee /> </span>
                  <span v-else>{{ room?.activeStory?.votes[user.id] }}</span>
                </div>
              </template>

              <v-list-item-title>
                {{ user.username }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-row>

        <!-- USERS -->
        <v-row class="mt-8" dense>
          <v-col v-for="(val, key) in room.users" :key="key" class="d-flex justify-center">
            <v-chip
              :color="isVoted(val.id) ? 'success' : val.observer ? 'warning' : 'primary'"
              size="x-large"
              density="comfortable">
              <template #prepend>
                <crown v-if="room.owner === val.id" />
              </template>

              <div class="mx-4">
                {{ val.username }}
              </div>

              <template #append>
                <three-dot v-if="isUserAdmin" :id="`chip${val.id}`" class="cursor-pointer" />
              </template>
            </v-chip>

            <v-menu v-if="isUserAdmin" :activator="`#chip${val.id}`">
              <v-list density="compact" rounded>
                <v-list-item density="compact" @click="makeObserver(val.id)">
                  {{ val.observer ? 'Join' : 'Observer' }}
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
        </v-row>

        <!-- Story panel -->
        <v-row>
          <v-toolbar class="mt-4">
            <v-toolbar-title> Stories</v-toolbar-title>
            <v-spacer />
            <v-btn @click="observe">{{ auth.observer ? 'Join' : 'Observe' }}</v-btn>
            <div v-if="isUserAdmin" class="mx-2">
              <v-btn v-if="!room.activeStory" @click="start">Start</v-btn>
              <v-btn v-if="allUserVoted" @click="restartVote">Restart</v-btn>
              <v-btn v-if="allUserVoted" @click="next">Next</v-btn>
              <v-btn v-if="room.started" @click="skip">Skip</v-btn>
            </div>
          </v-toolbar>
          <v-list class="w-100">
            <v-list-item
              v-for="(value, key) in room.stories"
              :key="key"
              :value="value"
              :active="room?.activeStory?.id === key || value.estimated"
              :color="value.estimated ? 'warning' : 'success'">
              <v-list-item-title>
                {{ value.title }}
                <span v-if="value.estimated">(Estimated - {{ value.estimation }})</span>
              </v-list-item-title>
              <template #append>
                <v-btn
                  v-if="isUserAdmin && !value.estimated && room?.activeStory?.id !== value.id"
                  variant="plain"
                  color="error"
                  @click="() => removeStory(key)">
                  Remove
                </v-btn>
              </template>
            </v-list-item>
          </v-list>
          <v-row v-if="isUserAdmin">
            <v-divider class="my-4" />
            <v-form v-model="isAddStoryValid" class="w-100" @submit.prevent="addStory">
              <v-text-field v-model="storyTitle" label="Title" :rules="[notNull]">
                <template #append>
                  <v-btn-group>
                    <v-btn type="submit">add</v-btn>
                  </v-btn-group>
                </template>
              </v-text-field>
            </v-form>
          </v-row>
        </v-row>
      </v-container>
    </v-card>
  </v-container>

  <!-- DIALOGS -->
  <v-dialog v-model="showCreateRoom" persistent max-width="500px">
    <v-card>
      <v-card-title> Create New Room</v-card-title>
      <v-card-text>
        <create-room @on-save="() => addUserToRoom()" />
      </v-card-text>
    </v-card>
  </v-dialog>

  <v-dialog v-model="showLoginDialog" persistent max-width="500px">
    <v-card>
      <v-card-title>Pick an username</v-card-title>
      <v-card-text>
        <login-comp @on-save="onUsernameSave" />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped></style>
